import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { SlaEditor } from '@/features/settings/SlaEditor'
import {
  SEVERITIES,
  dayFromNumber,
  dayNumber,
  formatBusinessHours,
  holidayLabels,
  londonToday,
  renewalFlag,
  renewalFlaggedFrom,
  renewalState,
  severityDefinitions,
  severityLabels,
  termMonths,
} from '@/lib/sla'
import type { RenewalState } from '@/lib/sla'
import { formatDate } from '@/lib/dates'
import { useClientDashboard } from '@/queries/clientDashboards'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useSlaSettings, useStaffSettings } from '@/queries/settings'
import type { CompanySla, TargetDuration } from '@/types/sla'
import { DashCard, Legend, Tip, tdClass, thClass } from './dashboardParts'
import { RenewalBadge } from './RenewalBadge'
import { LoadError } from './LoadError'

const BANNER_TINTS = { warning: 'bg-etn-warning-subtle', danger: 'bg-etn-danger-subtle', neutral: 'bg-etn-neutral-subtle' } as const

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`
const targetText = (target: TargetDuration) => plural(target.value, target.unit === 'hours' ? 'hour' : 'day')

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-etn-text-label">{label}</dt>
      <dd className="text-etn-text">{children}</dd>
    </>
  )
}

const NotSet = () => <span className="text-etn-text-muted">Not set</span>

function RenewalBanner({ sla, state, clientName, onEdit }: { sla: CompanySla; state: RenewalState; clientName: string; onEdit: () => void }) {
  const flag = renewalFlag(state)
  if (!flag) return null
  const text =
    state.kind === 'upcoming'
      ? `Ends ${formatDate(state.endDate)}. Agree the new term with ${clientName}, then update the dates. Nothing changes in the help desk when the date passes.`
      : state.kind === 'ended'
        ? `${plural(state.days, 'day')} ago. ${sla.enabled ? 'SLAs keep running as normal' : 'SLAs are switched off for this client'}; update the dates once the renewal is agreed.`
        : sla.startDate
          ? 'SLAs are on but have no end date. Add it so the renewal can be flagged.'
          : 'SLAs are on but have no start or end date. Add them so the renewal can be flagged.'
  return (
    <div data-testid="renewal-banner" className={`mb-4 flex flex-wrap items-center gap-3.5 rounded-etn-card px-5 py-4 ${BANNER_TINTS[flag.tone]}`}>
      <div className="min-w-0">
        <RenewalBadge state={state} slasOff={!sla.enabled} className="text-[13px]" />
        <p className="mt-1.5 max-w-[70ch] text-[13px] text-etn-text">{text}</p>
      </div>
      <Button size="sm" variant="outline" className="ml-auto" onClick={onEdit}>
        {state.kind === 'nodates' ? 'Add dates' : 'Update dates'}
      </Button>
    </div>
  )
}

const BAR_COLOURS = { passed: 'var(--etn-green-600)', flaggedPassed: 'var(--etn-amber-500)', flaggedAhead: 'var(--etn-amber-100)', ahead: 'var(--etn-gray-100)' }

// Split the term at the flag date and at today, each part labelled with its dates and length for the hover text.
function termSegments(startDate: string, endDate: string, today: string) {
  const start = dayNumber(startDate)
  const after = dayNumber(endDate) + 1
  const flagged = Math.max(start, dayNumber(renewalFlaggedFrom(endDate)))
  const now = Math.min(Math.max(dayNumber(today), start), after)
  return [
    { key: 'passed', label: 'Term so far', from: start, to: Math.min(now, flagged), colour: BAR_COLOURS.passed },
    { key: 'ahead', label: 'Still to come before the renewal is flagged', from: now, to: flagged, colour: BAR_COLOURS.ahead },
    { key: 'flaggedPassed', label: 'Renewal flagged, so far', from: flagged, to: now, colour: BAR_COLOURS.flaggedPassed },
    { key: 'flaggedAhead', label: 'Renewal flagged, still to come', from: Math.max(now, flagged), to: after, colour: BAR_COLOURS.flaggedAhead },
  ]
    .filter(segment => segment.to > segment.from)
    .map(segment => ({ ...segment, days: segment.to - segment.from, dates: `${formatDate(dayFromNumber(segment.from))} to ${formatDate(dayFromNumber(segment.to - 1))}` }))
}

function TermBar({ startDate, endDate, today }: { startDate: string; endDate: string; today: string }) {
  const segments = termSegments(startDate, endDate, today)
  const span = dayNumber(endDate) + 1 - dayNumber(startDate)
  const inTerm = today >= startDate && today <= endDate
  return (
    <div className="mt-4">
      <div className="relative">
        <div className="flex h-2.5 overflow-hidden rounded-etn-pill" role="img" aria-label={`Term from ${formatDate(startDate)} to ${formatDate(endDate)}`}>
          {segments.map(segment => (
            <Tip key={segment.key} label={`${segment.label}, ${segment.dates}`} value={plural(segment.days, 'day')} colour={segment.colour}>
              <span
                aria-label={`${segment.label}, ${segment.dates}: ${plural(segment.days, 'day')}`}
                style={{ flexGrow: segment.days, flexBasis: 0, background: segment.colour }}
              />
            </Tip>
          ))}
        </div>
        {inTerm && (
          <span
            aria-hidden
            className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-etn-text"
            style={{ left: `${((dayNumber(today) - dayNumber(startDate)) / span) * 100}%` }}
          />
        )}
      </div>
      <div className="mt-2 flex justify-between gap-2 text-[11.5px] text-etn-text-label">
        <span>{formatDate(startDate)}</span>
        <span>Flagged from {formatDate(renewalFlaggedFrom(endDate))}</span>
        <span>{formatDate(endDate)}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Legend
          items={[
            { key: 'passed', label: 'Term so far', colour: BAR_COLOURS.passed },
            { key: 'flagged', label: 'Last month: renewal flagged', colour: BAR_COLOURS.flaggedPassed },
            { key: 'ahead', label: 'Still to come', colour: BAR_COLOURS.ahead },
          ]}
        />
        {inTerm && (
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-etn-text-label">
            <span aria-hidden className="inline-block h-3 w-0.5 rounded-full bg-etn-text" />
            Today {formatDate(today)}
          </span>
        )}
      </div>
    </div>
  )
}

function ContractTerm({ sla, state, today, onEdit }: { sla: CompanySla; state: RenewalState; today: string; onEdit: () => void }) {
  const renewal =
    state.kind === 'current' ? (
      <>
        In {plural(state.days, 'day')} <span className="text-etn-text-muted">· flagged from {formatDate(renewalFlaggedFrom(state.endDate))}</span>
      </>
    ) : renewalFlag(state) ? (
      <RenewalBadge state={state} slasOff={!sla.enabled} />
    ) : (
      <NotSet />
    )
  return (
    <DashCard
      testId="contract-term"
      title="Contract term"
      subtitle="The SLA's start and end dates"
      right={
        <Button size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
      }
    >
      <div className="px-[18px] py-4">
        <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-[13px]">
          <Prop label="Start">{sla.startDate ? formatDate(sla.startDate) : <NotSet />}</Prop>
          <Prop label="End">{sla.endDate ? formatDate(sla.endDate) : <NotSet />}</Prop>
          <Prop label="Length">{sla.startDate && sla.endDate ? plural(termMonths(sla.startDate, sla.endDate), 'month') : <NotSet />}</Prop>
          <Prop label="Renewal">{renewal}</Prop>
        </dl>
        {sla.startDate && sla.endDate && <TermBar startDate={sla.startDate} endDate={sla.endDate} today={today} />}
      </div>
    </DashCard>
  )
}

function SlaPolicy({ sla }: { sla: CompanySla }) {
  const staff = useStaffSettings()
  const contact = sla.primaryContactId ? staff.data?.admins.find(admin => admin._id === sla.primaryContactId) : null
  return (
    <DashCard
      testId="sla-policy"
      title="SLA policy"
      subtitle="Applies to tickets raised from now on"
      right={<Badge variant={sla.enabled ? 'success' : 'neutral'}>{sla.enabled ? 'On' : 'Off'}</Badge>}
    >
      <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 px-[18px] py-4 text-[13px]">
        <Prop label="SLAs">{sla.enabled ? 'On: new tickets get response and resolution targets' : 'Off: new tickets get no SLA'}</Prop>
        <Prop label="Primary contact">
          {sla.primaryContactId ? (
            <>
              <b className="font-medium">{contact ? (contact.name ?? contact.email ?? contact._id) : '…'}</b>{' '}
              <span className="text-etn-text-muted">· tagged on #client-support alerts</span>
            </>
          ) : (
            <span className="text-etn-text-muted">None</span>
          )}
        </Prop>
        <Prop label="Business hours">{formatBusinessHours(sla.businessHours)}</Prop>
        <Prop label="Bank holidays">{holidayLabels[sla.businessHours.holidays]}</Prop>
      </dl>
    </DashCard>
  )
}

function SlaTargets({ sla }: { sla: CompanySla }) {
  return (
    <DashCard testId="sla-targets" title="Targets" subtitle="In business time. New tickets start at Moderate; staff change the severity on the ticket.">
      <table className="w-full">
        <thead>
          <tr>
            <th scope="col" className={thClass}>
              Severity
            </th>
            <th scope="col" className={thClass}>
              Respond within
            </th>
            <th scope="col" className={thClass}>
              Resolve within
            </th>
          </tr>
        </thead>
        <tbody>
          {SEVERITIES.map(severity => (
            <tr key={severity} className="border-b border-[var(--etn-gray-100)] last:border-0">
              <td className={`${tdClass} py-2.5`}>
                <div className="font-medium text-etn-text">{severityLabels[severity]}</div>
                <div className="text-[12px] text-etn-text-muted">{severityDefinitions[severity]}</div>
              </td>
              <td className={tdClass}>{targetText(sla.targets[severity].response)}</td>
              <td className={tdClass}>{targetText(sla.targets[severity].resolution)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-[var(--etn-gray-100)] px-[18px] py-2.5 text-[12px] text-etn-text-muted">
        Also editable on the help desk&apos;s Settings page: both change the same setting.
      </div>
    </DashCard>
  )
}

export function ContractSlaView({ companyId }: { companyId: string }) {
  const settings = useSlaSettings()
  const dashboard = useClientDashboard(companyId)
  const [editing, setEditing] = useState(false)
  const clientName = dashboard.data?.data.companyName ?? ''

  if (settings.error && !settings.data) return <LoadError title={extractErrorMessage(settings.error, "The SLA didn't load")} onRetry={() => settings.refetch?.()} />
  if (!settings.data) return null

  const row = settings.data.data.find(entry => entry.companyId === companyId)
  const today = londonToday()
  const state = renewalState(row?.sla, today)
  const edit = () => setEditing(true)

  return (
    <div>
      {row ? (
        <>
          <RenewalBanner sla={row.sla} state={state} clientName={clientName} onEdit={edit} />
          <div className="mb-4 grid items-start gap-4 lg:grid-cols-2">
            <ContractTerm sla={row.sla} state={state} today={today} onEdit={edit} />
            <SlaPolicy sla={row.sla} />
          </div>
          <SlaTargets sla={row.sla} />
        </>
      ) : (
        <div className="rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper">
          <EmptyState
            icon={<FileText />}
            title={`No SLA set up for ${clientName}`}
            body="Their tickets get no response or resolution targets, and there is no contract term to track."
          />
          <div className="-mt-16 pb-8 text-center">
            <Button size="sm" onClick={edit}>
              Set up SLA
            </Button>
          </div>
        </div>
      )}
      <Dialog open={editing} onOpenChange={open => !open && setEditing(false)}>
        {editing && <SlaEditor editing={{ companyId, name: clientName, sla: row?.sla ?? settings.data.defaults }} onClose={() => setEditing(false)} />}
      </Dialog>
    </div>
  )
}
